import type StoreService from '@ember-data/store';
import type DraftDocumentModel from 'garaje/models/draft-document';
import { pollWithDeadline, REQUEST_TIMEOUT_MS, uploadFile, withRequestDeadline } from 'garaje/utils/document-uploads';

// Re-exported for the create-flow consumers (draft-document model + document
// review card) that imported these from here before the neutral extraction;
// this module's public surface is unchanged.
export { REQUEST_TIMEOUT_MS, uploadFile, withRequestDeadline };

export interface RequestUploadLinkParams {
  templateId: string;
  locationId?: string | null;
  contentType: string;
  byteSize: number;
  timeoutMs?: number;
}

export interface UploadLink {
  draftDocumentId: string;
  uploadLink: string;
}

interface DraftDocumentDocument {
  data: {
    id: string;
    type: 'draft-documents';
    attributes: Record<string, unknown>;
  };
}

function draftDocumentAdapter(store: StoreService) {
  return store.adapterFor('draft-document');
}

/**
 * Stateless transport for the admin pre-reg draft-document flow over the
 * `/api/v3/draft-documents` action endpoints. Plain functions taking `store`
 * (the existing utils idiom — see `notify-workflow.ts`): the flow has no
 * shared state, so per the review on the introducing PR it lives here rather
 * than a singleton service. The consuming component sequences
 * request-link → upload → confirm inside its own ember-concurrency task (so
 * each step is a cancellation point), mirroring the direct-uploader/
 * active-storage shape.
 *
 * Errors propagate to the caller (the card owns loading/error UI); this layer
 * never swallows them or substitutes a silent fallback — including a stalled
 * upload and a parse timeout, which reject rather than hanging.
 */

/**
 * Reserve a draft-document and obtain a write-only upload URL for the file.
 *
 * Creates a `draft-document-upload-link` resource (its adapter's `pathForType`
 * points at the nested `draft-documents/upload-links` collection), so Ember Data
 * serializes the JSON:API envelope — `content-type` / `byte-size` attributes and
 * the `template` / `location` relationships — instead of hand-assembling it. The
 * `template` is required and the `location` (when supplied) are read from the
 * store as relationship references; the card renders the loaded template record,
 * so a missing template is a programmer error, not a runtime fallback.
 */
export async function requestUploadLink(
  store: StoreService,
  { templateId, locationId, contentType, byteSize, timeoutMs = REQUEST_TIMEOUT_MS }: RequestUploadLinkParams,
): Promise<UploadLink> {
  const template = store.peekRecord('custom-user-document-template', templateId);
  if (!template) {
    throw new Error(`Draft document upload-link requires custom-user-document-template ${templateId} in the store`);
  }

  const uploadLinkRecord = store.createRecord('draft-document-upload-link', {
    contentType,
    byteSize,
    template,
  });

  // Only attach the location relationship when a loaded record is available, so the
  // create envelope omits it entirely (rather than sending `data: null`) when absent.
  if (locationId) {
    const location = store.peekRecord('location', locationId);
    if (location) {
      uploadLinkRecord.location = location;
    }
  }

  try {
    await withRequestDeadline(uploadLinkRecord.save(), 'Draft document upload-link save', timeoutMs);

    const draftDocumentId = uploadLinkRecord.belongsTo('draftDocument').id();
    const uploadLink = uploadLinkRecord.uploadLink;
    if (!draftDocumentId || !uploadLink) {
      throw new Error('Draft document upload-link response missing draft-document id or upload-link');
    }
    return { draftDocumentId, uploadLink };
  } finally {
    // The record is a one-shot envelope: once the id + write-capable upload URL are
    // read (or the save/validation failed), don't leave it lingering in the store.
    // A timed-out save is still in flight — Ember Data throws on unload-while-saving;
    // that abandoned record settles with the request and nothing reads it.
    if (!uploadLinkRecord.isSaving) {
      uploadLinkRecord.unloadRecord();
    }
  }
}

/**
 * Confirm the uploaded file and begin server-side parsing. Resolves with the
 * draft-document in `processing` (it may already be `completed`).
 *
 * Stays on `adapter.ajax` rather than `apiAction`: the upload-link response links
 * the draft by id only (no `included`), so the draft isn't materialized as a record
 * until this call's `pushPayload`. `apiAction` requires an existing record to build
 * its URL — which doesn't exist yet at confirm-time — so the id-string form is used.
 */
export async function confirmDraftDocument(
  store: StoreService,
  draftDocumentId: string,
  { templateId, flowId, timeoutMs = REQUEST_TIMEOUT_MS }: { templateId: string; flowId: string; timeoutMs?: number },
): Promise<DraftDocumentModel> {
  const adapter = draftDocumentAdapter(store);
  const url = `${adapter.buildURL('draft-document', draftDocumentId)}/confirm`;
  // Per the BE contract: a resource identifier in `data` carrying the chosen
  // template and flow as relationships. confirm re-reads the draft from the
  // store after pushPayload.
  const payload = (await withRequestDeadline(
    adapter.ajax(url, 'POST', {
      data: {
        data: {
          type: 'draft-documents',
          id: draftDocumentId,
          relationships: {
            template: {
              data: { type: 'custom-user-document-templates', id: templateId },
            },
            flow: {
              data: { type: 'flows', id: flowId },
            },
          },
        },
      },
    }),
    'Draft document confirm',
    timeoutMs,
  )) as DraftDocumentDocument;

  store.pushPayload('draft-document', payload);

  const draftDocument = store.peekRecord('draft-document', draftDocumentId);
  if (!draftDocument) {
    throw new Error(`Draft document ${draftDocumentId} missing from store after confirm`);
  }

  return draftDocument;
}

/**
 * Single GET against the draft-document (always reloads from the server).
 */
export async function pollDraftDocument(store: StoreService, draftDocumentId: string): Promise<DraftDocumentModel> {
  return store.findRecord('draft-document', draftDocumentId, { reload: true });
}

/**
 * A single poll bounded by `deadlineMs`, delegating to the neutral
 * `pollWithDeadline` racer. The "… parsing timed out" message is preserved so
 * existing callers and tests are unaffected.
 */
export function pollDraftDocumentWithDeadline(
  store: StoreService,
  draftDocumentId: string,
  deadlineMs: number,
): Promise<DraftDocumentModel> {
  return pollWithDeadline(
    () => pollDraftDocument(store, draftDocumentId),
    `Draft document ${draftDocumentId} parsing`,
    deadlineMs,
  );
}
